/**
 * Cart Sample Selector Component - Simplified with localStorage persistence
 */
class CartSampleSelector {
  constructor() {
    this.selectedSamples = [];
    this.currentSlot = null;
    this.storageKey = 'cart-sample-selections';
    this.init();
  }

  init() {
    this.loadFromStorage();
    this.bindEvents();
    this.bindCartEvents();
    this.restoreUIState();
  }

  bindEvents() {
    document.addEventListener('click', (e) => {
      // Placeholder clicks
      if (e.target.closest('.cart-sample-selector__placeholder')) {
        e.preventDefault();
        const slot = e.target.closest('.cart-sample-selector__slot');
        this.openModal(slot);
        return;
      }

      // Remove button clicks  
      if (e.target.closest('.cart-sample-selector__remove-button')) {
        const slot = e.target.closest('.cart-sample-selector__slot');
        this.removeSample(slot);
        return;
      }

      // Sample item clicks
      const sampleItem = e.target.closest('.cart-sample-selector__sample-item');
      if (sampleItem && !sampleItem.hasAttribute('data-unavailable')) {
        this.selectSample(sampleItem);
        return;
      }

      // Modal close clicks
      if (e.target.closest('.cart-sample-selector__modal-close') || 
          e.target.closest('.cart-sample-selector__modal-overlay')) {
        this.closeModal();
        return;
      }

      // Change samples button click
      if (e.target.closest('.cart-sample-selector__change-button')) {
        this.changeSamples();
        return;
      }
    });

    // Keyboard events
    document.addEventListener('keydown', (e) => {
      if (e.target.closest('.cart-sample-selector__placeholder') && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        const slot = e.target.closest('.cart-sample-selector__slot');
        this.openModal(slot);
      }
      if (e.key === 'Escape') {
        const modal = document.getElementById('sample-selector-modal');
        if (modal && modal.style.display !== 'none') {
          this.closeModal();
        }
      }
    });
  }

  openModal(slot) {
    this.currentSlot = slot;
    const modal = document.getElementById('sample-selector-modal');
    if (modal) {
      modal.style.display = 'flex';
      document.body.style.overflow = 'hidden';
    }
  }

  closeModal() {
    const modal = document.getElementById('sample-selector-modal');
    if (modal) {
      modal.style.display = 'none';
      document.body.style.overflow = '';
      this.currentSlot = null;
    }
  }

  selectSample(element) {
    if (!this.currentSlot) return;

    const sample = {
      id: element.dataset.sampleId,
      variantId: element.dataset.sampleVariantId,
      name: element.dataset.sampleName,
      size: element.dataset.sampleSize,
      image: element.querySelector('.cart-sample-selector__sample-image img').src,
      slot: this.currentSlot.dataset.slot
    };

    // Remove existing sample from this slot
    this.selectedSamples = this.selectedSamples.filter(s => s.slot !== sample.slot);
    
    // Add new sample
    this.selectedSamples.push(sample);

    this.renderSlot(this.currentSlot, sample);
    this.updateCounter();
    this.saveToStorage();
    this.closeModal();

    // Auto-add to cart when 2 samples selected
    if (this.selectedSamples.length === 2) {
      this.addToCart();
    }
  }

  removeSample(slot) {
    const slotNumber = slot.dataset.slot;
    this.selectedSamples = this.selectedSamples.filter(sample => sample.slot !== slotNumber);
    this.renderEmptySlot(slot);
    this.updateCounter();
    this.saveToStorage();
  }

  renderSlot(slot, sample) {
    const placeholder = slot.querySelector('.cart-sample-selector__placeholder');
    const selectedItem = slot.querySelector('.cart-sample-selector__selected-item');
    
    if (placeholder && selectedItem) {
      placeholder.style.display = 'none';
      selectedItem.style.display = 'flex';
      
      const image = selectedItem.querySelector('.cart-sample-selector__image');
      const name = selectedItem.querySelector('.cart-sample-selector__item-name');
      const size = selectedItem.querySelector('.cart-sample-selector__item-size');
      
      if (image) image.src = sample.image;
      if (image) image.alt = sample.name;
      if (name) name.textContent = sample.name;
      if (size) size.textContent = sample.size;
    }
  }

  renderEmptySlot(slot) {
    const placeholder = slot.querySelector('.cart-sample-selector__placeholder');
    const selectedItem = slot.querySelector('.cart-sample-selector__selected-item');
    
    if (placeholder && selectedItem) {
      placeholder.style.display = 'flex';
      selectedItem.style.display = 'none';
    }
  }

  updateCounter() {
    const counter = document.getElementById('sample-counter');
    const changeWrapper = document.querySelector('.cart-sample-selector__change-wrapper');
    const removeButtons = document.querySelectorAll('.cart-sample-selector__remove-button');
    
    if (counter) {
      counter.textContent = this.selectedSamples.length;
    }
    
    // Show "Change Samples" button only when 2 samples are selected
    if (changeWrapper) {
      changeWrapper.style.display = this.selectedSamples.length === 2 ? 'block' : 'none';
    }
    
    // Hide X buttons when both samples are selected (2/2)
    removeButtons.forEach(button => {
      button.style.display = this.selectedSamples.length === 2 ? 'none' : 'block';
    });
  }

  addToCart() {
    if (this.selectedSamples.length === 0) return;
    
    const cartItems = this.selectedSamples.map(sample => ({
      id: sample.variantId,
      quantity: 1,
      properties: { '_sample': 'true' }
    }));
    
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: cartItems })
    })
    .then(response => response.json())
    .then(() => {
      // Dispatch cart update events
      document.dispatchEvent(new CustomEvent('cart:updated'));
      if (window.publish && window.PUB_SUB_EVENTS) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-sample-selector' });
      }
      
      // Reload with parameter to auto-open cart drawer
      window.location.href = window.location.href + (window.location.href.includes('?') ? '&' : '?') + 'samples_added=true';
    })
    .catch(error => console.error('Error adding samples:', error));
  }

  async changeSamples() {
    if (this.selectedSamples.length === 0) return;

    try {
      // Get current cart to find sample items
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      
      // Find sample items in cart
      const sampleItems = cart.items.filter(item => 
        item.properties && item.properties._sample === 'true'
      );

      if (sampleItems.length === 0) {
        // No samples in cart, just clear localStorage and refresh
        this.clearStorage();
        this.refreshWithDrawerOpen();
        return;
      }

      // Remove sample items from cart by setting quantity to 0
      const updates = {};
      sampleItems.forEach(item => {
        updates[item.key] = 0;
      });

      // Update cart to remove samples
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });

      // Clear localStorage and refresh page with drawer open
      this.clearStorage();
      this.refreshWithDrawerOpen();

    } catch (error) {
      console.error('Error changing samples:', error);
    }
  }

  refreshWithDrawerOpen() {
    // Refresh with parameter to auto-open cart drawer
    window.location.href = window.location.href + (window.location.href.includes('?') ? '&' : '?') + 'samples_changed=true';
  }

  // localStorage persistence methods
  saveToStorage() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.selectedSamples));
    } catch (error) {
      console.warn('Could not save samples to localStorage:', error);
    }
  }

  loadFromStorage() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        this.selectedSamples = JSON.parse(stored);
      }
    } catch (error) {
      console.warn('Could not load samples from localStorage:', error);
      this.selectedSamples = [];
    }
  }

  clearStorage() {
    try {
      localStorage.removeItem(this.storageKey);
    } catch (error) {
      console.warn('Could not clear samples from localStorage:', error);
    }
  }

  // Cart event handling
  bindCartEvents() {
    // Listen for multiple cart update events
    document.addEventListener('cart:updated', () => {
      setTimeout(() => this.syncWithCart(), 100);
    });
    
    // Listen for Shopify's publish/subscribe cart updates
    if (window.PUB_SUB_EVENTS && window.subscribe) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event.source !== 'cart-sample-selector') {
          setTimeout(() => this.syncWithCart(), 100);
        }
      });
    }
    
    // Listen for manual cart changes (quantity updates, removes)
    document.addEventListener('change', (e) => {
      if (e.target.name === 'updates[]' || e.target.closest('cart-remove-button')) {
        setTimeout(() => this.syncWithCart(), 200);
      }
    });
    
    // Listen for remove button clicks specifically  
    document.addEventListener('click', (e) => {
      if (e.target.closest('cart-remove-button')) {
        setTimeout(() => this.syncWithCart(), 200);
      }
    });
  }

  async syncWithCart() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      // VALIDATION: Check if qualifying products (full-size-fragrance) are still in cart
      // If not, remove all samples to prevent free sample abuse
      const hasQualifyingProduct = this.cartHasQualifyingProduct(cart);

      // Check if our samples are still in cart
      const sampleItems = cart.items.filter(item =>
        item.properties && item.properties._sample === 'true'
      );

      // Get collection info for thorough checking
      const sampleCollectionId = window.sampleCollectionInfo?.collectionId;
      const samplesByCollection = sampleCollectionId ?
        cart.items.filter(item =>
          item.product_id && window.sampleProducts?.some(p => p.id == item.product_id)
        ) : [];

      // Combine both checks - by property and by collection
      const allSampleItems = [...sampleItems, ...samplesByCollection].filter((item, index, arr) =>
        arr.findIndex(i => i.id === item.id) === index
      );

      // If no qualifying product but samples exist, remove samples from cart
      if (!hasQualifyingProduct && allSampleItems.length > 0) {
        console.log('[Sample Selector] No qualifying product in cart, removing samples...');
        await this.removeAllSamplesFromCart(allSampleItems);
        this.selectedSamples = [];
        this.saveToStorage();
        this.restoreUIState();
        return;
      }

      const currentSampleCount = this.selectedSamples.length;
      const cartSampleCount = allSampleItems.length;

      // If sample counts don't match, sync the state
      if (cartSampleCount !== currentSampleCount) {
        if (cartSampleCount === 0) {
          // No samples in cart, clear selections
          this.selectedSamples = [];
          this.saveToStorage();
          this.restoreUIState();
        } else if (cartSampleCount < currentSampleCount) {
          // Some samples were removed, find which ones
          const cartSampleIds = allSampleItems.map(item => item.variant_id.toString());
          this.selectedSamples = this.selectedSamples.filter(sample =>
            cartSampleIds.includes(sample.variantId)
          );
          this.saveToStorage();
          this.restoreUIState();
        }
      }

      // Dispatch custom event for other components
      document.dispatchEvent(new CustomEvent('sample-selector:synced', {
        detail: { sampleCount: allSampleItems.length, selectedCount: this.selectedSamples.length }
      }));

    } catch (error) {
      console.warn('Could not sync with cart:', error);
    }
  }

  // Check if cart contains at least one qualifying product (with full-size-fragrance tag)
  cartHasQualifyingProduct(cart) {
    // Get the list of qualifying variant IDs from Liquid (set on page load)
    const qualifyingVariantIds = window.qualifyingProductVariantIds || [];

    // Also check against sample product IDs to exclude them
    const sampleProductIds = (window.sampleProducts || []).map(p => p.id);

    // Check if any non-sample item in cart is a qualifying product
    // We check variant_id against the initial qualifying list
    // AND we also do a secondary check: any item NOT in samples collection
    // that costs more than $30 is likely a qualifying product
    const hasQualifyingVariant = cart.items.some(item => {
      // Skip items that are samples
      if (item.properties && item.properties._sample === 'true') return false;
      if (sampleProductIds.includes(item.product_id)) return false;

      // Check if this variant was in the initial qualifying list
      if (qualifyingVariantIds.includes(item.variant_id)) return true;

      // Fallback: Check by price threshold ($30+) and not a sample
      // This helps catch cases where cart was modified via API
      if (item.price >= 3000 && !sampleProductIds.includes(item.product_id)) {
        return true;
      }

      return false;
    });

    return hasQualifyingVariant;
  }

  // Remove all sample items from cart
  async removeAllSamplesFromCart(sampleItems) {
    if (sampleItems.length === 0) return;

    const updates = {};
    sampleItems.forEach(item => {
      updates[item.key] = 0;
    });

    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });

      // Dispatch cart update events
      document.dispatchEvent(new CustomEvent('cart:updated'));
      if (window.publish && window.PUB_SUB_EVENTS) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-sample-selector-cleanup' });
      }

      // Reload page to reflect changes
      setTimeout(() => {
        window.location.reload();
      }, 300);
    } catch (error) {
      console.error('Error removing samples from cart:', error);
    }
  }

  // UI state restoration
  restoreUIState() {
    // Clear all slots first
    document.querySelectorAll('.cart-sample-selector__slot').forEach(slot => {
      this.renderEmptySlot(slot);
    });

    // Restore selected samples to their slots
    this.selectedSamples.forEach(sample => {
      const slot = document.querySelector(`[data-slot="${sample.slot}"]`);
      if (slot) {
        this.renderSlot(slot, sample);
      }
    });

    this.updateCounter();
  }
}

// Simple one-time initialization
document.addEventListener('DOMContentLoaded', () => {
  window.cartSampleSelector = new CartSampleSelector();
  
  // Auto-open cart drawer if samples were just added or changed
  if (window.location.href.includes('samples_added=true') || window.location.href.includes('samples_changed=true')) {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      setTimeout(() => cartDrawer.open(), 100);
    }
    // Clean up URL parameters
    let cleanUrl = window.location.href.replace(/[?&]samples_added=true/, '');
    cleanUrl = cleanUrl.replace(/[?&]samples_changed=true/, '');
    window.history.replaceState({}, document.title, cleanUrl);
  }
});